import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	ElementRef,
	EventEmitter,
	HostListener,
	inject,
	input,
	Input,
	OnDestroy,
	OnInit,
	Output,
	Signal,
	signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IBuilding } from '@shared/interfaces/building.interfaces';
import { LocationTreeNodeComponent } from '@shared/ui-components/au-locations-tree-selector/location-tree-node/location-tree-node.component';
import {
	ILocationNode,
	ILocationTreeNode,
	LocationTreeNodeType,
	LocationTreeSelectorService,
} from '@shared/ui-components/au-locations-tree-selector/location-tree-selector.service';
import { AuUtilsFunctions } from '@app/shared';
import { decrementIndex, incrementIndex } from '@app/core/utils/list-navigation-utils';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LocationService } from '@app/pages/cmms/services/location.service';
import { AuSearchInputComponent } from '../au-search-input/au-search-input.component';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';

export interface ICMMSLocationTree {
	locationId: string;
	locationType: string;
	name: string;
}

@Component({
	selector: 'au-locations-tree-selector',
	imports: [
		CommonModule,
		LocationTreeNodeComponent,
		MatProgressSpinnerModule,
		AuSearchInputComponent,
		NgxTolgeeModule,
		MatButtonModule,
	],
	templateUrl: './au-locations-tree-selector.component.html',
	styleUrls: ['./au-locations-tree-selector.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [LocationTreeSelectorService],
})
export class AuLocationsTreeSelectorComponent implements OnInit, OnDestroy {
	private readonly locationTreeService = inject(LocationTreeSelectorService);
	private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
	private readonly destroyRef = inject(DestroyRef);
	private readonly locationService = inject(LocationService);
	private readonly unavailableLocations: ILocationNode[] = [];

	@Input() actionCenter: boolean = false;
	@Input() locationInputList: ICMMSLocationTree[] = [];
	@Input() disableMultipleSelect = false;
	protected allowDeselectForSingleSelect = input(true);

	disableNestedSelection = input<boolean>(false);
	@Output() locationsChanged = new EventEmitter<ICMMSLocationTree[]>();

	searchText$ = new BehaviorSubject<string>('');
	searchText = toSignal(this.searchText$.pipe(debounceTime(300), distinctUntilChanged()));
	locations = toSignal(this.locationService.buildings$, {
		initialValue: [],
	});
	filteredLocations = computed(() => {
		return this.locations().filter(name =>
			this.matchesSearchText(name, this.searchText())
		);
	});
	index = signal(0);
	visibleNodes: Signal<LocationTreeNodeType[]> = computed(() => {
		const visibleNodes: LocationTreeNodeType[] = [];
		for (const location of this.locations()) {
			visibleNodes.push(location);
			if (this.isExpanded(location.id)) {
				for (const floor of location.floor) {
					visibleNodes.push(floor);
					if (this.isExpanded(floor.id)) {
						for (const room of floor.room) {
							visibleNodes.push(room);
						}
					}
				}
			}
		}
		return visibleNodes;
	});
	blockKeyboardMovement: boolean = false;

	ngOnInit() {
		this.convertBuildingsToTreeNodeMap(this.locations());
		this.updateNodeMapWithProvidedLocations();

		this.locationTreeService.expanded$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.scrollToActiveElement();
			});
	}

	updateSelectedLocations(node: ILocationTreeNode) {
		if (this.disableMultipleSelect) {
			this.locationTreeService.deselectAllNodesExcept(node.id);
			if (!node.selected) {
				this.locationsChanged.emit(this.unavailableLocations);
			} else {
				this.locationsChanged.emit([
					{
						locationId: node.id,
						name: node.name,
						locationType: AuUtilsFunctions.capitalizeText(node.contentType),
					},
					...this.unavailableLocations,
				]);
			}
			return;
		}

		this.locationTreeService.selectNodeAndItsChildren(node, node.selected);
		if (node.parentId) {
			this.checkChildrenAndUpdateParent(node.parentId);
		}
		if (node.grandparentId) {
			this.checkChildrenAndUpdateParent(node.grandparentId);
		}
		this.locationsChanged.emit([
			...this.locationTreeService.getFilteredLocations(),
			...this.unavailableLocations,
		]);
	}

	allChildrenSelected(parentNode: ILocationTreeNode): boolean {
		return parentNode.children.every(
			child => this.locationTreeService.getNode(child)?.selected
		);
	}

	checkChildrenAndUpdateParent(id: string) {
		const parentNode = this.locationTreeService.getNode(id);
		parentNode.selected = this.allChildrenSelected(parentNode);
		this.locationTreeService.updateFlatTreeNodeMap(id, parentNode);
	}

	isExpanded(locationId: string): boolean {
		return this.locationTreeService.getNode(locationId)?.isExpanded;
	}

	hasChildren(locationId: string): boolean {
		return this.locationTreeService.getNode(locationId)?.children.length > 0;
	}

	private updateNodeMapWithProvidedLocations() {
		this.locationInputList?.forEach(inputLocation => {
			const node = this.locationTreeService.getNode(inputLocation.locationId);
			if (!node) {
				this.unavailableLocations.push(inputLocation);
			} else {
				this.locationTreeService.selectNodeAndItsChildren(node, true);
			}
		});
	}

	private convertBuildingsToTreeNodeMap(buildings: IBuilding[]) {
		// TODO Web Workers seems to not work due to Cross origin error
		// 	If Someone has good will you can try to fix it and then move this
		// 	method to a web worker created in ./workers folder
		buildings.forEach(building => {
			const node = this.formatLocation(building);
			this.locationTreeService.updateFlatTreeNodeMap(node.id, node);
			building.floor?.forEach(floor => {
				const node = this.formatLocation(floor, building.id);
				this.locationTreeService.updateFlatTreeNodeMap(node.id, node);
				floor.room.forEach(room => {
					const node = this.formatLocation(room, floor.id, building.id);
					this.locationTreeService.updateFlatTreeNodeMap(node.id, node);
				});
			});
		});
	}

	private formatLocation(
		location: LocationTreeNodeType,
		parentId: string = null,
		grandparentId: string = null
	): ILocationTreeNode {
		return {
			id: location.id,
			name: location.name,
			contentType: location.contentType,
			children: this.getLocationChildrenIds(location),
			selected: false,
			isExpanded: false,
			parentId,
			grandparentId,
		};
	}

	private getLocationChildrenIds(location: LocationTreeNodeType): string[] {
		if ('floor' in location) {
			const floorIds = location.floor?.map(item => item.id) || [];
			const roomIds =
				location.floor?.flatMap(item => item.room.map(room => room.id)) || [];
			return [...floorIds, ...roomIds];
		}
		if ('room' in location) {
			return location.room.map(room => room.id);
		}
		return [];
	}

	ngOnDestroy() {
		this.locationTreeService.clearFlatTreeNodeMap();
	}

	@HostListener('window:keydown', ['$event'])
	keyboardNav(event: KeyboardEvent) {
		if (this.blockKeyboardMovement) {
			return;
		}
		if (event.key === 'ArrowUp') {
			this.index.set(decrementIndex(this.index(), this.visibleNodes.length));
		}
		if (event.key === 'ArrowDown') {
			this.index.set(incrementIndex(this.index(), this.visibleNodes.length));
		}
		setTimeout(() => {
			this.scrollToActiveElement();
		}, 100);
		// ' ' is space key apparently
		const action = this.visibleNodes()[this.index()];
		if (event.key === ' ') {
			return this.locationTreeService.triggerToggleCheckbox(action);
		}
		if (event.key === 'Control') {
			return this.locationTreeService.openLocationChildren(action);
		}
	}

	scrollToActiveElement() {
		const activeElement = this.el.nativeElement.querySelector('.active');

		if (activeElement) {
			activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	}

	isActive(node: LocationTreeNodeType) {
		const index = this.visibleNodes().indexOf(node);
		return index === this.index() && this.actionCenter;
	}

	updateFilteredData(searchText = '') {
		this.searchText$.next(searchText.trim().toLowerCase());
	}

	matchesSearchText(node: IBuilding, filterValue: string): boolean {
		return node.name.toLowerCase().includes(filterValue);
	}

	protected clearSelections() {
		this.locationTreeService.deselectAllNodes();
		this.locationsChanged.emit(this.unavailableLocations);
	}
}
